import type { LatLngBoundsLiteral } from 'leaflet';

/**
 * Caja de México (con islas y costas) — la misma que usa el API
 * (`src/util/mexicoCoords.ts`). Sirve para que un punto con coordenadas
 * malas (una fuente que publicó la longitud sin signo) no mande el mapa
 * al océano: el "mapa gris" que se veía al centrar en resultados.
 */
export const MEXICO_BBOX = { minLng: -118.6, maxLng: -86.5, minLat: 14.4, maxLat: 32.8 } as const;

/** [lngMin, latMin, lngMax, latMax] — el formato de los FlyRequest. */
export const MEXICO_BBOX_ARRAY: [number, number, number, number] = [
  MEXICO_BBOX.minLng, MEXICO_BBOX.minLat, MEXICO_BBOX.maxLng, MEXICO_BBOX.maxLat,
];

/**
 * Hasta dónde se deja arrastrar el mapa: México con margen amplio (se
 * ve la frontera y el Caribe), pero no medio planeta vacío.
 */
export const MAP_MAX_BOUNDS: LatLngBoundsLiteral = [[0, -140], [45, -60]];

export function insideMexico(lng: number, lat: number): boolean {
  return Number.isFinite(lng) && Number.isFinite(lat)
    && lng >= MEXICO_BBOX.minLng && lng <= MEXICO_BBOX.maxLng
    && lat >= MEXICO_BBOX.minLat && lat <= MEXICO_BBOX.maxLat;
}

/**
 * Caja [lngMin, latMin, lngMax, latMax] de los puntos que caen en México;
 * null si ninguno. Los que caen fuera se ignoran en vez de estirar la caja.
 */
export function mexicoExtent(points: Iterable<[number, number]>): { bbox: [number, number, number, number]; n: number } | null {
  let lngMin = Infinity, latMin = Infinity, lngMax = -Infinity, latMax = -Infinity, n = 0;
  for (const [lng, lat] of points) {
    if (!insideMexico(lng, lat)) continue;
    if (lng < lngMin) lngMin = lng;
    if (lng > lngMax) lngMax = lng;
    if (lat < latMin) latMin = lat;
    if (lat > latMax) latMax = lat;
    n++;
  }
  return n > 0 ? { bbox: [lngMin, latMin, lngMax, latMax], n } : null;
}

/** ¿La caja toca México? Un destino totalmente fuera se descarta. */
export function bboxTouchesMexico([lngMin, latMin, lngMax, latMax]: [number, number, number, number]): boolean {
  return lngMax >= MEXICO_BBOX.minLng && lngMin <= MEXICO_BBOX.maxLng
    && latMax >= MEXICO_BBOX.minLat && latMin <= MEXICO_BBOX.maxLat;
}
